import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { SanityService, type PortfolioData, type SanityPerson, type SanityProject, type SanityCertification, type SanityEducation, type SanityWorkExperience, type SanityFreelance } from '../services/sanity';

interface SanityDataContextType {
  person: SanityPerson | null;
  projects: SanityProject[];
  certifications: SanityCertification[];
  education: SanityEducation[];
  workExperience: SanityWorkExperience[];
  freelance: SanityFreelance | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const defaultContextValue: SanityDataContextType = {
  person: null,
  projects: [],
  certifications: [],
  education: [],
  workExperience: [],
  freelance: null,
  isLoading: true,
  error: null,
  refetch: async () => {},
};

const SanityDataContext = createContext<SanityDataContextType>(defaultContextValue);

interface SanityDataProviderProps {
  children: React.ReactNode;
}

export const SanityDataProvider: React.FC<SanityDataProviderProps> = ({ children }) => {
  const [data, setData] = useState<PortfolioData>({
    person: null,
    projects: [],
    certifications: [],
    education: [],
    workExperience: [],
    freelance: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const portfolioData = await SanityService.fetchPortfolioData();
      setData(portfolioData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data from Sanity';
      console.error('SanityDataContext fetch error:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const contextValue: SanityDataContextType = {
    person: data.person,
    projects: data.projects,
    certifications: data.certifications,
    education: data.education,
    workExperience: data.workExperience,
    freelance: data.freelance,
    isLoading,
    error,
    refetch: fetchData,
  };

  return (
    <SanityDataContext.Provider value={contextValue}>
      {children}
    </SanityDataContext.Provider>
  );
};

export const useSanityData = (): SanityDataContextType => {
  const context = useContext(SanityDataContext);
  if (context === undefined) {
    throw new Error('useSanityData must be used within a SanityDataProvider');
  }
  return context;
};

export default SanityDataContext;
